import { useEffect } from 'react';

type Props = {
  password: string;
  confirm: string;
  onValidityChange: (isValid: boolean) => void;
};

export function PasswordRulesPopover({ password, confirm, onValidityChange }: Props) {
  const rules = {
    minLength: password.length >= 8,
    maxLength: password.length <= 64,
    hasNumber: /\d/.test(password),
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasSymbol: /[!@#$%^&*(),.?":;{}|<>\/\-_\\]/.test(password),
    matches: confirm !== undefined ? password === confirm : undefined,
  };

  const hasViolation = Object.values(rules).some((v) => v === false);
  const isValid = password.length > 0 && !hasViolation;

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  if (password.length === 0 || !hasViolation) return null;

  return (
    <div className="relative h-0">
      <div className="absolute left-0 right-0 z-20 rounded-sm bg-[var(--color-text-input-background)] p-4 shadow-lg">
        <Rule ok={rules.minLength} text="Includes at least 8 characters" />
        <Rule ok={rules.maxLength} text="No more than 64 characters" />
        <Rule ok={rules.hasNumber} text="Includes number" />
        <Rule ok={rules.hasLower} text="Includes lowercase letter" />
        <Rule ok={rules.hasUpper} text="Includes uppercase letter" />
        <Rule ok={rules.hasSymbol} text="Includes special symbol" />
        {rules.matches !== undefined && <Rule ok={rules.matches} text="Passwords match" />}
      </div>
    </div>
  );
}

function Rule({ ok, text }: { ok: boolean; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 mt-2 text-sm ${ok ? 'text-[var(--color-input-success)]' : 'text-[var(--color-input-error)]'}`}
    >
      <span className="font-bold">{ok ? '✓' : '✕'}</span>
      <span>{text}</span>
    </div>
  );
}
